import * as React from 'react'
import { motion } from 'framer-motion'
import { cn } from '../../utils/cn'
import type { Variant, Size } from '../../types'

/**
 * JPG Academy Button
 *
 * Uses a native <button> to avoid framer-motion hydration mismatches
 * on the disabled attribute. Motion is used only for the loading spinner.
 */

const variantStyles: Record<Variant, string> = {
	default: 'bg-cream text-navy-700 hover:bg-cream-dark border border-cream-dark',
	primary: 'bg-navy text-cream-50 hover:bg-navy-600 shadow-sm',
	secondary: 'bg-maroon text-cream-50 hover:bg-maroon-600 shadow-sm',
	ghost: 'bg-transparent hover:bg-cream text-navy-700',
	outline: 'bg-transparent text-navy border border-navy hover:bg-navy hover:text-cream-50',
	destructive: 'bg-error text-cream-50 hover:bg-error/90 shadow-sm',
}

// Size heights (h-8 / h-10 / h-12) are intentionally kept in sync
// with Input `inputSizeStyles` so that buttons align when placed inline.
const sizeStyles: Record<Size, string> = {
	sm: 'h-8 px-3 text-xs gap-1.5 rounded-md',
	md: 'h-10 px-4 text-sm gap-2 rounded-md',
	lg: 'h-12 px-6 text-base gap-2.5 rounded-lg',
	xl: 'h-14 px-8 text-lg gap-3 rounded-lg',
}

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: Variant
	size?: Size
	loading?: boolean
	icon?: React.ReactNode
	fullWidth?: boolean
}

const SpinnerSvg = () => (
	<svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
		<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
		<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
	</svg>
)

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant = 'default', size = 'md', loading = false, icon, fullWidth = false, children, disabled, ...rest }, ref) => {
		const isDisabled = disabled || loading
		return (
			<button
				ref={ref}
				className={cn(
					'inline-flex items-center justify-center font-ui font-semibold',
					'transition-colors duration-fast ease-default',
					'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/40 focus-visible:ring-offset-2 focus-visible:ring-offset-cream',
					'disabled:opacity-50 disabled:pointer-events-none',
					variantStyles[variant],
					sizeStyles[size],
					fullWidth && 'w-full',
					className,
				)}
				disabled={isDisabled}
				suppressHydrationWarning
				{...rest}
			>
				{loading ? (
					<motion.span className="flex items-center justify-center" animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
						<SpinnerSvg />
					</motion.span>
				) : icon ? (
					<span className="shrink-0">{icon}</span>
				) : null}
				{children}
			</button>
		)
	},
)

Button.displayName = 'Button'
